import type { OrderAction, UserType } from '@/types';

// Core's audit DTO uses these names; older gateways used the UI aliases.
export type OrderActionWire = Partial<OrderAction> & {
  id: number;
  orderId: number;
  actionOn: string;
  orderActionEnum?: number;
  orderActionEnumString?: string;
  userTypeEnum?: UserType;
  actionByUserName?: string;
  actionByImagePath?: string;
  actionDescription?: string;
};

export function normalizeOrderAction(item: OrderActionWire): OrderAction {
  return {
    ...item,
    actionTypeId: item.orderActionEnum ?? item.actionTypeId ?? 0,
    actionType: item.orderActionEnumString ?? item.actionType ?? '',
    actionByUserId: item.actionByUserId ?? '',
    actionByName: item.actionByUserName ?? item.actionByName,
    actionByUserImage: item.actionByImagePath ?? item.actionByUserImage,
    actionByUserType: item.userTypeEnum ?? item.actionByUserType,
    note: item.actionDescription ?? item.note,
  };
}

export interface BookingRescheduleHistory {
  previousTime: string | null;
  newTime: string;
  reason: string | null;
}

export function parseBookingReschedule(note?: string): BookingRescheduleHistory | null {
  if (!note) return null;
  try {
    const value: unknown = JSON.parse(note);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const data = value as Record<string, unknown>;
    if (data.action !== 'booking_rescheduled' || typeof data.newTime !== 'string') return null;
    return {
      previousTime: typeof data.previousTime === 'string' ? data.previousTime : null,
      newTime: data.newTime,
      reason: typeof data.reason === 'string' && data.reason.trim() ? data.reason.trim() : null,
    };
  } catch {
    return null;
  }
}

export function formatBookingWallTime(value: string | null, locale: string): string | null {
  if (!value) return null;
  // Booking audit timestamps record tenant wall time. Never shift an appointment
  // because the administrator's browser is in a different timezone.
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?$/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number);
  if (year < 1000 || month < 1 || month > 12 || day < 1 || hour > 23 || minute > 59) return null;
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute));
  if (date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return new Intl.DateTimeFormat(locale.startsWith('ar') ? 'ar' : 'en', { numberingSystem: 'latn',
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'UTC',
  }).format(date);
}
